import {
  AgxService,
  LogEntrySeverityLevel,
  PoisonQueueMessage,
  useGetLogsForServiceAtPeriod,
} from '@urbanx/agx.support.client';
import { AgxCheckbox, AgxTable, AgxTypography, IColumnConfig } from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

interface PoisonQueueLogsProps {
  service?: AgxService;
  selectedQueueMessage?: PoisonQueueMessage;
}

const logsColumns: (string | IColumnConfig)[] = ['Timestamp', 'Log Level', { name: 'Message' }];

const getSeverityLevelColour = (level: LogEntrySeverityLevel | undefined) => {
  switch (level) {
    case LogEntrySeverityLevel.Critical:
    case LogEntrySeverityLevel.Error:
      return 'rgba(var(--color-red-200))';
    case LogEntrySeverityLevel.Warning:
      return 'rgba(var(--color-yellow-100))';
    case LogEntrySeverityLevel.Information:
    case LogEntrySeverityLevel.Verbose:
    default:
      return 'rgba(var(--color-blue-100))';
  }
};

const PoisonQueueLogs = ({ service, selectedQueueMessage }: PoisonQueueLogsProps) => {
  const [errorsOnly, setErrorsOnly] = useState(true);

  const dateToSearchFor = useMemo(() => {
    const insertionTime = selectedQueueMessage?.insertionTime;

    if (!insertionTime) return undefined;

    if (typeof insertionTime === 'string' || typeof insertionTime === 'number') {
      return new Date(insertionTime);
    }

    return insertionTime;
  }, [selectedQueueMessage]);

  const { data: logMessages, isPending: isLogMessagesPending } = useGetLogsForServiceAtPeriod(
    service!,
    dateToSearchFor ?? new Date(),
    !!dateToSearchFor && !!service
  );

  const logRows = useMemo(() => {
    if (!logMessages) return [];

    return logMessages
      .filter((log) => !errorsOnly || log.severityLevel === LogEntrySeverityLevel.Error)
      .map((log, idx) => {
        const severityLevelColour = getSeverityLevelColour(log.severityLevel);

        return {
          backgroundColor: severityLevelColour,
          Timestamp: (
            <AgxTypography id={`poison-queue-log-timestamp-${idx}`} type={'body_ui'} size={'small'}>
              {log.timestamp ? dayjs(log.timestamp).format('DD/MM/YYYY h:mm A') : '-'}
            </AgxTypography>
          ),
          'Log Level': (
            <AgxTypography id={`poison-queue-log-level-${idx}`} type={'body_ui'} size={'small'}>
              {log.severityLevel ?? '-'}
            </AgxTypography>
          ),
          Message: (
            <AgxTypography id={`poison-queue-log-message-${idx}`} type={'body_ui'} size={'small'}>
              {[log.message, log.exceptionMessage].filter((m) => m && m.length > 0).join(' ')}
            </AgxTypography>
          ),
        };
      });
  }, [logMessages, errorsOnly]);

  if (!service) return null;

  return (
    <Panel title={'Poison Queue Logs'}>
      <AgxCheckbox
        id={'errorsOnlyToggle'}
        checked={errorsOnly}
        onChange={setErrorsOnly}
        label="Show Errors Only"
      />
      <AgxTable columns={logsColumns} rows={logRows} showLoadingState={isLogMessagesPending} />
    </Panel>
  );
};

export default PoisonQueueLogs;
